import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { adminApi } from "../../services/adminApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { DataTable } from "../../components/common/DataTable";
import { GapNotice } from "../../components/common/GapNotice";

export function PerformanceMonitoringPage() {
  const [slowQueries, setSlowQueries] = useState(null);
  const [storage, setStorage] = useState(null);

  useEffect(() => {
    adminApi.slowQueries().then((r) => setSlowQueries(r.slow_queries)).catch(() => setSlowQueries([]));
    adminApi.storageForecast().then(setStorage).catch(() => setStorage(null));
  }, []);

  if (!slowQueries) return <Spinner />;

  return (
    <div>
      <h1>Performance Monitoring</h1>

      <Card title="Top 10 Slowest Endpoints" className="section-spacing">
        <DataTable
          rows={slowQueries}
          rowKey="endpoint"
          emptyTitle="No performance data recorded yet"
          columns={[
            { key: "endpoint", header: "Endpoint" },
            { key: "avg_response_time", header: "Avg (ms)", render: (r) => Math.round(r.avg_response_time) },
            { key: "p95_response_time", header: "p95 (ms)", render: (r) => (r.p95_response_time != null ? Math.round(r.p95_response_time) : "—") },
            { key: "error_rate", header: "Error Rate", render: (r) => `${(r.error_rate * 100).toFixed(2)}%` },
            { key: "request_count", header: "Requests" },
          ]}
        />
      </Card>

      <div className="section-spacing" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
        <Card title="API Response Times by Endpoint">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={slowQueries} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="endpoint" width={140} />
              <Tooltip />
              <Bar dataKey="avg_response_time" fill="#3b82c4" name="Avg ms" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Error Rates by Endpoint">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={slowQueries} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="endpoint" width={140} />
              <Tooltip formatter={(v) => `${(v * 100).toFixed(2)}%`} />
              <Bar dataKey="error_rate" fill="#d64545" name="Error Rate" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Storage Growth Forecasting" className="section-spacing">
        {storage ? (
          <>
            <StatTile label="Current Storage" value={`${storage.current_storage_mb} MB`} />
            <div style={{ marginTop: "var(--space-3)" }}>
              <DataTable
                rows={storage.by_table}
                rowKey="table_name"
                columns={[
                  { key: "table_name", header: "Table" },
                  { key: "reserved_mb", header: "Size (MB)", render: (r) => r.reserved_mb.toFixed(2) },
                  { key: "row_count", header: "Rows" },
                ]}
              />
            </div>
            <GapNotice>{storage.note}</GapNotice>
          </>
        ) : (
          <Spinner />
        )}
      </Card>

      <Card title="Database Health, Function Performance & App Service Metrics">
        <GapNotice>
          Index usage/fragmentation/deadlock stats need queries against sys.dm_db_index_usage_stats /
          sys.dm_db_index_physical_stats / sys.dm_exec_query_stats (available in the DB right now, no new schema
          needed, just a new admin.py endpoint similar to storage_forecast's existing sys.dm_db_partition_stats
          query). Function cold-start/execution-duration and App Service CPU/memory/request-count both live in Azure
          Monitor, not this SQL database at all -- they need an Azure Monitor Metrics REST API call from a backend
          proxy (same API-key-must-stay-server-side reasoning as appInsightsApi.js), which doesn't exist yet.
        </GapNotice>
      </Card>

      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Note: "API response times by endpoint filtered by theme" isn't possible with today's data -- audit.PerformanceMetrics
        (V016) has no theme dimension, so per-theme breakdowns would need that collection pipeline extended first.
      </p>
    </div>
  );
}
